import type { GetStaticProps } from 'next';
import React from 'react';

import { AboutNav } from '../components/AboutNav';
import Layout from '../components/Layout';
import { Static } from '../components/PageTypes/Static';
import { ABOUT_PAGE_TYPE } from '../constants';
import { imageOgStaticLoader } from '../helpers/og-image';
import { StaticPage } from '../types/StaticContentType';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../utils/storyblok';
import { pageTranslations } from '../utils/translations';

type PageProps = {
  locale: string;
  locales: string[];
  preview: boolean;
  story: StaticPage;
  ogImageUrl: string;
};

const Page = ({ locale, locales, ogImageUrl, preview, story }: PageProps) => {
  const page = useStoryblok(story, preview, locale);

  return (
    <Layout
      description={page.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={page.content.seo?.title}
    >
      <AboutNav />
      <Static page={page} pageName={ABOUT_PAGE_TYPE} />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({
  locale = 'en',
  locales,
  preview = false,
}) => {
  const page: StoryResponse<StaticPage> = await getStoryblokPage(
    'cdn/stories/about',
    {
      locale,
      preview,
    },
  );

  if (!page) return { notFound: true };

  const poster = (page.data.story.content.intro || []).find(
    (blok) => blok.component === 'inlineVideo',
  ) as InlineVideoBlok;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: page.data.story.content.seo.title,
    imageSrc:
      poster?.cover?.filename ||
      'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      story: page.data?.story,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale, ['og'])),
    },
  };
};

export default Page;
